/**
 * One EDITABLE configuration row (D2).
 *
 * A sibling of `BexioSettingsField` rather than a mode of it. That component
 * reports a stored value and is what a manager — and every provider-id field —
 * still sees; this one asks for input. Keeping them apart means the read-only
 * surface cannot grow form semantics by accident, and neither file has to carry
 * a branch explaining which half of itself is live.
 *
 * It renders the same `<div>` inside the parent `<dl>`, with the `<dt>` holding
 * a real `<label htmlFor>`. So the row keeps its term/definition semantics for a
 * screen reader AND the control gets a properly associated label — which a bare
 * `<dt>` would not provide, since `<dt>` is not a labelling element.
 *
 * NO PROVIDER-CATALOG CONTROL LIVES HERE. There is no datalist, no id picker and
 * no invented plain-text option: the six Bexio ids stay read-only until D3 can
 * resolve them from Bexio itself.
 */
import React from "react";

import { normalizeTriState, triStateToSelectValue } from "../bexioSettingsView";

/** Shared by every control, so heights and focus rings cannot drift apart. */
const CONTROL =
  "w-full min-w-0 rounded-lg border border-input bg-background px-2.5 py-1.5 " +
  "text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 " +
  "focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-60";

const CONTROL_INVALID = "border-amber-500 dark:border-amber-400";

export default function BexioSettingsControl({
  id,
  label,
  hint,
  control,
  value,
  error,
  missing = false,
  disabled = false,
  onChange,
}) {
  const describedBy =
    [error ? `${id}-error` : null, hint ? `${id}-hint` : null]
      .filter(Boolean)
      .join(" ") || undefined;

  const common = {
    id,
    disabled,
    "aria-describedby": describedBy,
    "aria-invalid": error ? true : undefined,
    className: `${CONTROL} ${error ? CONTROL_INVALID : ""}`,
  };

  let field = null;
  if (control === "tristate") {
    field = (
      <select
        {...common}
        value={triStateToSelectValue(value)}
        onChange={(e) => onChange(normalizeTriState(e.target.value))}
      >
        <option value="">Nicht konfiguriert</option>
        <option value="true">Ja</option>
        <option value="false">Nein</option>
      </select>
    );
  } else if (control === "number") {
    field = (
      <input
        {...common}
        type="number"
        // `min` is a hint to the browser, not the rule: the backend is the
        // authority and 0 is a valid MWST type, so this must not be `min=1`.
        min="0"
        step="1"
        inputMode="numeric"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  } else if (control === "month") {
    field = (
      <input
        {...common}
        type="month"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  } else {
    field = (
      <input
        {...common}
        type="text"
        maxLength={64}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }

  return (
    <div className="min-w-0 border-b border-border py-3 last:border-b-0">
      <dt className="text-xs font-medium text-muted-foreground">
        <label htmlFor={id}>{label}</label>
        {missing ? (
          <span className="ml-2 whitespace-nowrap text-xs font-medium text-amber-800 dark:text-amber-300">
            fehlt
          </span>
        ) : null}
      </dt>
      <dd className="mt-1 min-w-0">
        {field}
        {error ? (
          <span
            id={`${id}-error`}
            className="mt-1 block text-xs text-amber-800 dark:text-amber-300"
          >
            {error}
          </span>
        ) : null}
        {hint ? (
          <span id={`${id}-hint`} className="mt-0.5 block text-xs text-muted-foreground">
            {hint}
          </span>
        ) : null}
      </dd>
    </div>
  );
}
